import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ReactNode, useCallback, useState } from 'react';
import { useRevalidator } from 'react-router';

import ContactEditDialog from '~/components/portfolio/ContactEditDialog';
import { PropertyIdentityFields } from '~/components/portfolio/detailPage/PropertyIdentityFields';
import { PortfolioPerformanceBand } from '~/components/portfolio/PortfolioPerformanceBand';
import type { PropertyDetails } from '~/components/portfolio/types';
import { propertiesPartialUpdate } from '~/data/generated/endpoints/properties/properties';
import type { ContactInput } from '~/data/schemas/contact';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { AvailableKpis } from '~/lib/portfolio/getKpiFilter';
import { BUTTON_SX, pageBandColor, pageBandDividerColor, SURFACE_RADIUS } from '~/theme';

interface PropertyDetailPageHeaderProps {
  property: PropertyDetails | null;
  /** `null` in the row preview, which closes from its own title bar. */
  onBack: (() => void) | null;
  backLabel: string;
  compact?: boolean;
  /** How many elevators the property has, or `undefined` while that is in flight. */
  elevatorCount: number | undefined;
  footer?: ReactNode;
}

/**
 * Page header: which property this is, the record around it, and how it is performing
 * over the selected period. The KPI strip replaces the Overview tab, whose four cards
 * were the only thing on it.
 */
export function PropertyDetailPageHeader({
  property,
  onBack,
  backLabel,
  elevatorCount,
  compact = false,
  footer,
}: PropertyDetailPageHeaderProps) {
  const terms = useLocaleTerms();
  const [contactOpen, setContactOpen] = useState(false);
  const revalidator = useRevalidator();

  // The page's data comes from the route loader, so a saved contact is picked up by
  // revalidating rather than by a refetch this component owns.
  const onSaveContact = useCallback(
    async (data: ContactInput) => {
      if (!property) return;
      await propertiesPartialUpdate(property.id, { contact: data });
      void revalidator.revalidate();
    },
    [property, revalidator],
  );

  return (
    <Box
      px={compact ? 2 : { xs: 2, md: 3 }}
      py={compact ? 2 : { xs: 2, md: 3 }}
      sx={
        compact
          ? { bgcolor: pageBandColor }
          : {
              bgcolor: pageBandColor,
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: SURFACE_RADIUS,
              borderTopRightRadius: SURFACE_RADIUS,
            }
      }
    >
      <Box display="flex" alignItems="flex-start" gap={3}>
        {onBack ? (
          <Box flexShrink={0} ml={-1}>
            <Tooltip title={backLabel}>
              <IconButton onClick={onBack} aria-label={backLabel} size="small">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        <Box flex={1} minWidth={0}>
          <Stack gap="3px" alignItems="flex-start">
            {elevatorCount === undefined ? null : (
              <Chip
                size="small"
                variant="outlined"
                label={`${elevatorCount} ${elevatorCount === 1 ? terms.unit : terms.elevators}`}
              />
            )}

            <Typography variant="h5" noWrap>
              {property ? (
                property.name
              ) : (
                <Skeleton width={260} sx={{ fontSize: '1.5rem' }} animation="wave" />
              )}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {property ? property.address : <Skeleton width={220} />}
            </Typography>

            <Box
              display={compact ? 'none' : 'flex'}
              alignItems="center"
              gap={0.5}
              ml={-0.5}
              mt={0.5}
            >
              <Button
                size="small"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setContactOpen(true)}
                disabled={!property}
                sx={BUTTON_SX}
              >
                Edit contact
              </Button>
            </Box>

            <Divider flexItem sx={{ mt: 1, borderColor: pageBandDividerColor }} />
          </Stack>

          <Box mt={{ xs: 2, md: 3 }}>
            <PropertyIdentityFields property={property} />
          </Box>

          <Divider flexItem sx={{ mt: { xs: 2, md: 3 }, borderColor: pageBandDividerColor }} />

          {/* `mx` cancels the tiles' own leading padding so the first figure lines up with the
              fields above it and the tabs below. */}
          <Box mt={1} mx={{ xs: -1, md: -1.5 }}>
            <PortfolioPerformanceBand
              bare
              summary={property?.summary ?? null}
              propertyId={property?.id}
              perRow={compact ? 3 : undefined}
              kpisToShow={[
                AvailableKpis.maintenanceCompletion,
                AvailableKpis.maintenanceVisit,
                AvailableKpis.emergencyCalls,
                AvailableKpis.callbacks,
              ]}
            />
          </Box>
          {footer}
        </Box>
      </Box>

      {contactOpen && property ? (
        <ContactEditDialog
          open
          onClose={() => setContactOpen(false)}
          onSave={onSaveContact}
          contact={property.contact}
        />
      ) : null}
    </Box>
  );
}
